import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Upload, Trash2, FileText, Calendar, Video, Users, Eye, Activity, Archive, Settings, UserX, Download, Image } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Lazy loading states for users
  const [usersPagination, setUsersPagination] = useState({
    offset: 0,
    limit: 50,
    total_count: 0,
    has_more: false
  });
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  
  // State for aggregate statistics
  const [userStatistics, setUserStatistics] = useState({
    total_users: 0,
    total_visualizations: 0,
    active_users: 0
  });
  const [usersError, setUsersError] = useState("");
  const [activeTab, setActiveTab] = useState("playlists"); // "playlists", "users", "main-files", "settings", "imagen", "evangelio", or "about"
  
  // Main files states
  const [mainFileZip, setMainFileZip] = useState(null);
  const [mainFileUploadLoading, setMainFileUploadLoading] = useState(false);
  const [mainFileUploadMessage, setMainFileUploadMessage] = useState("");

  // Optional files states
  const [optionalFileZip, setOptionalFileZip] = useState(null);
  const [optionalFileUploadLoading, setOptionalFileUploadLoading] = useState(false);
  const [optionalFileUploadMessage, setOptionalFileUploadMessage] = useState("");

  // Settings states
  const [emailValidationEnabled, setEmailValidationEnabled] = useState(true);
  const [emailValidationLoading, setEmailValidationLoading] = useState(false);
  const [emailValidationMessage, setEmailValidationMessage] = useState("");

  // News configuration states
  const [newsConfig, setNewsConfig] = useState({
    public: { enabled: false, title: "", description: "" },
    private: { enabled: false, title: "", description: "" }
  });
  const [newsConfigLoading, setNewsConfigLoading] = useState(false);
  const [newsConfigMessage, setNewsConfigMessage] = useState("");

  // Image configuration states
  const [imageConfig, setImageConfig] = useState({
    imageUrl: "",
    clickUrl: "",
    tooltipText: "Click to watch playlist",
    altText: "Featured Image",
    visible: true
  });
  const [imageConfigLoading, setImageConfigLoading] = useState(false);
  const [imageConfigMessage, setImageConfigMessage] = useState("");

  // Evangelio states  
  const [evangelioFile, setEvangelioFile] = useState(null);
  const [evangelioUploadLoading, setEvangelioUploadLoading] = useState(false);
  const [evangelioUploadMessage, setEvangelioUploadMessage] = useState("");
  const [evangelios, setEvangelios] = useState([]);
  const [evangeliosLoading, setEvangeliosLoading] = useState(false);
  const [selectedEvangelio, setSelectedEvangelio] = useState(null);
  const [evangelioModalOpen, setEvangelioModalOpen] = useState(false);
  const [evangelioButtonEnabled, setEvangelioButtonEnabled] = useState(true);
  const [evangelioButtonLoading, setEvangelioButtonLoading] = useState(false);
  const [evangelioButtonMessage, setEvangelioButtonMessage] = useState("");

  // Topics Button Configuration states
  const [topicsButtonEnabled, setTopicsButtonEnabled] = useState(true);
  const [topicsButtonLoading, setTopicsButtonLoading] = useState(false);
  const [topicsButtonMessage, setTopicsButtonMessage] = useState("");

  // Temas states
  const [temasFile, setTemasFile] = useState(null);
  const [temasLoading, setTemasLoading] = useState(false);
  const [temasMessage, setTemasMessage] = useState("");
  const [temasUploading, setTemasUploading] = useState(false);
  const [temas, setTemas] = useState([]);
  const [temasListLoading, setTemasListLoading] = useState(false);

  // User deletion states
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // {user: userObject}
  const [deleteMessage, setDeleteMessage] = useState("");

  // About states
  const [aboutContent, setAboutContent] = useState('');
  const [aboutLoading, setAboutLoading] = useState(false);
  const [aboutMessage, setAboutMessage] = useState('');
  const [aboutFile, setAboutFile] = useState(null);
  const [aboutUploadLoading, setAboutUploadLoading] = useState(false);

  const { isAuthenticated, userType, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (!isAuthenticated || userType !== "admin") {
      navigate("/login");
    }
  }, [isAuthenticated, userType, navigate]);

  // Fetch uploaded playlists
  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const response = await axios.get(`${API}/admin/playlists`);
        setPlaylists(response.data);
      } catch (err) {
        console.error("Error fetching playlists:", err);
        setError("Error loading playlists");
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPlaylists();
    }
  }, [isAuthenticated]);

  // Fetch users with stats - initial load
  const fetchUsers = async (reset = true) => {
    try {
      if (reset) {
        setUsersLoading(true);
        setUsers([]);
        setUsersPagination({
          offset: 0,
          limit: 50,
          total_count: 0,
          has_more: false
        });
      } else {
        setLoadingMoreUsers(true);
      }
      
      setUsersError("");
      const response = await axios.get(`${API}/admin/users`, {
        params: {
          offset: reset ? 0 : usersPagination.offset,
          limit: usersPagination.limit
        }
      });
      
      if (reset) {
        setUsers(response.data.users);
        setUsersPagination(response.data.pagination);
        // Update statistics from API response
        if (response.data.statistics) {
          setUserStatistics(response.data.statistics);
        }
      } else {
        setUsers(prevUsers => [...prevUsers, ...response.data.users]);
        setUsersPagination(response.data.pagination);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      const errorMessage = "Error loading users";
      if (reset) {
        setError(errorMessage);
      } else {
        setUsersError(errorMessage);
      }
    } finally {
      if (reset) {
        setUsersLoading(false);
      } else {
        setLoadingMoreUsers(false);
      }
    }
  };

  // Download users CSV
  const downloadUsersCSV = async () => {
    try {
      console.log('CSV download function started');
      setError(""); // Clear any previous errors
      
      // Fetch ALL users data using pagination (API limit is 100)
      console.log('Fetching users data...');
      let allUsers = [];
      let offset = 0;
      const limit = 100; // Maximum allowed by API
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(`${API}/admin/users`, {
          params: {
            offset: offset,
            limit: limit
          }
        });
        
        console.log(`Fetched ${response.data.users.length} users (offset: ${offset})`);
        allUsers = allUsers.concat(response.data.users);
        
        hasMore = response.data.pagination.has_more;
        offset += limit;
      }
      
      console.log(`Total users fetched: ${allUsers.length}`);
      const usersData = allUsers;
      
      // Convert JSON data to CSV format
      console.log('Converting to CSV format...');
      const csvContent = convertUsersToCSV(usersData);
      console.log('CSV content generated, length:', csvContent.length);
      
      // Create and download CSV file
      console.log('Creating download...');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('CSV download completed successfully');
      
    } catch (err) {
      console.error("Error downloading CSV:", err);
      setError("Error downloading CSV file. Please try again.");
    }
  };

  // Helper function to convert users data to CSV format
  const convertUsersToCSV = (users) => {
    // Define CSV headers
    const headers = [
      'ID',
      'Nombre',
      'Apellido', 
      'Email',
      'Fecha de Registro',
      'Estado',
      'Videos Vistos',
      'Volúmenes Accedidos',
      'Lista de Volúmenes',
      'Última Actividad'
    ];
    
    // Create CSV content
    let csvContent = headers.join(',') + '\n';
    
    users.forEach(user => {
      const row = [
        `"${user.id || ''}"`,
        `"${user.first_name || ''}"`,
        `"${user.last_name || ''}"`,
        `"${user.email || ''}"`,
        `"${user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : ''}"`,
        `"${user.is_active ? 'Activo' : 'Inactivo'}"`,
        `"${user.total_videos_watched || 0}"`,
        `"${user.volumes_accessed || 0}"`,
        `"${user.volumes_list ? user.volumes_list.join('; ') : ''}"`,
        `"${user.last_activity ? new Date(user.last_activity).toLocaleDateString('es-ES') : 'Nunca'}"`
      ];
      csvContent += row.join(',') + '\n';
    });
    
    return csvContent;
  };
    
  // Load more users for infinite scroll
  const loadMoreUsers = async () => {
    if (loadingMoreUsers || !usersPagination.has_more) return;
    
    const newOffset = usersPagination.offset + usersPagination.limit;
    setUsersPagination(prev => ({ ...prev, offset: newOffset }));
    
    await fetchUsers(false);
  };

  // Fetch users when switching to users tab
  useEffect(() => {
    if (activeTab === "users" && isAuthenticated) {
      fetchUsers(true);
    }
  }, [activeTab, isAuthenticated]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (activeTab !== "users" || !usersPagination.has_more || loadingMoreUsers) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && usersPagination.has_more && !loadingMoreUsers) {
          loadMoreUsers();
        }
      },
      {
        rootMargin: "100px", // Start loading 100px before reaching the bottom
        threshold: 0.1
      }
    );

    const sentinel = document.getElementById("users-scroll-sentinel");
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [activeTab, usersPagination.has_more, loadingMoreUsers]);

  // Fetch email validation status when switching to settings tab
  useEffect(() => {
    if (activeTab === "settings" && isAuthenticated) {
      fetchEmailValidationStatus();
      fetchNewsConfig();
      fetchTopicsButtonConfig();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch image configuration when switching to imagen tab
  useEffect(() => {
    if (activeTab === "imagen" && isAuthenticated) {
      fetchImageConfig();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch temas when switching to main-files tab
  useEffect(() => {
    if (activeTab === "main-files" && isAuthenticated) {
      fetchTemas();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch evangelios when switching to evangelio tab
  useEffect(() => {
    if (activeTab === "evangelio" && isAuthenticated) {
      fetchEvangelios();
      fetchEvangelioButtonConfig();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch about content when switching to about tab
  useEffect(() => {
    if (activeTab === "about" && isAuthenticated) {
      fetchAboutContent();
    }
  }, [activeTab, isAuthenticated]);

  // Fetch email validation status
  const fetchEmailValidationStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-validation-status`);
      setEmailValidationEnabled(response.data.email_validation_enabled);
    } catch (err) {
      console.error("Error fetching email validation status:", err);
      setEmailValidationMessage("Error loading email validation status");
    }
  };

  // Fetch news configuration
  const fetchNewsConfig = async () => {
    try {
      const response = await axios.get(`${API}/news-config`);
      setNewsConfig(response.data);
    } catch (err) {
      console.error("Error fetching news config:", err);
      setNewsConfigMessage("Error loading news configuration");
    }
  };

  // Fetch image configuration
  const fetchImageConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/image-config`);
      if (response.data.featuredImage) {
        setImageConfig(response.data.featuredImage);
      } else {
        // Reset to default if no configuration exists
        setImageConfig({
          imageUrl: "",
          clickUrl: "",
          tooltipText: "Click to watch playlist",
          altText: "Featured Image",
          visible: true
        });
      }
    } catch (err) {
      console.error("Error fetching image config:", err);
      setImageConfigMessage("Error loading image configuration");
    }
  };

  // Toggle email validation
  const toggleEmailValidation = async () => {
    setEmailValidationLoading(true);
    setEmailValidationMessage("");

    try {
      const response = await axios.post(`${API}/admin/toggle-email-validation`, {
        enabled: !emailValidationEnabled
      });

      setEmailValidationEnabled(response.data.email_validation_enabled);
      setEmailValidationMessage(
        `Validación de email ${response.data.email_validation_enabled ? "activada" : "desactivada"} exitosamente`
      );
    } catch (err) {
      console.error("Error toggling email validation:", err);
      setEmailValidationMessage("Error updating email validation setting");
    } finally {
      setEmailValidationLoading(false);
    }
  };

  // Save news configuration
  const saveNewsConfig = async () => {
    setNewsConfigLoading(true);
    setNewsConfigMessage("");

    try {
      const response = await axios.put(`${API}/admin/news-config`, newsConfig);

      if (response.data.success) {
        setNewsConfig(response.data.config);
        setNewsConfigMessage("Configuración de noticias guardada exitosamente");
      }
    } catch (err) {
      console.error("Error saving news config:", err);
      if (err.response?.data?.detail) {
        setNewsConfigMessage(err.response.data.detail);
      } else {
        setNewsConfigMessage("Error guardando configuración de noticias");
      }
    } finally {
      setNewsConfigLoading(false);
    }
  };

  // Save image configuration
  const saveImageConfig = async () => {
    setImageConfigLoading(true);
    setImageConfigMessage("");

    // Basic validation
    if (!imageConfig.imageUrl.trim() || !imageConfig.clickUrl.trim()) {
      setImageConfigMessage("La URL de imagen y URL de destino son requeridas");
      setImageConfigLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/image-config`, {
        imageUrl: imageConfig.imageUrl,
        clickUrl: imageConfig.clickUrl,
        tooltipText: imageConfig.tooltipText,
        altText: imageConfig.altText,
        visible: imageConfig.visible
      });

      if (response.data.message) {
        setImageConfigMessage("Configuración de imagen guardada exitosamente");
      }
    } catch (err) {
      console.error("Error saving image config:", err);
      if (err.response?.data?.detail) {
        setImageConfigMessage(err.response.data.detail);
      } else {
        setImageConfigMessage("Error guardando configuración de imagen");
      }
    } finally {
      setImageConfigLoading(false);
    }
  };

  // Temas Functions
  const handleTemasFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setTemasFile(file);
      setTemasMessage("");
    } else {
      setTemasMessage("Por favor selecciona un archivo ZIP válido");
      setTemasFile(null);
    }
  };

  const handleTemasUpload = async () => {
    if (!temasFile) return;

    setTemasUploading(true);
    setTemasMessage("");

    try {
      const formData = new FormData();
      formData.append("file", temasFile);

      const response = await axios.post(`${API}/admin/upload-temas`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        const { files_processed, files_failed, failed_files, processed_temas } = response.data;
        
        let message = `Temas cargados exitosamente: ${files_processed} archivos procesados`;
        
        if (files_failed > 0) {
          message += `, ${files_failed} fallaron`;
          if (failed_files.length > 0) {
            message += `\nArchivos fallidos: ${failed_files.join(', ')}`;
          }
        }
        
        if (processed_temas.length > 0) {
          message += `\nTemas procesados: ${processed_temas.join(', ')}`;
        }
        
        setTemasMessage(message);
        setTemasFile(null);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"][accept=".zip"]');
        if (fileInput) fileInput.value = '';
        
        // Refresh temas list
        await fetchTemas();
      }
    } catch (err) {
      console.error("Error uploading temas:", err);
      if (err.response?.data?.detail) {
        setTemasMessage(`Error: ${err.response.data.detail}`);
      } else {
        setTemasMessage("Error subiendo archivos de temas");
      }
    } finally {
      setTemasUploading(false);
    }
  };

  const fetchTemas = async () => {
    setTemasListLoading(true);
    try {
      const response = await axios.get(`${API}/admin/temas`);
      setTemas(response.data.temas);
    } catch (err) {
      console.error("Error fetching temas:", err);
      setTemasMessage("Error cargando lista de temas");
    } finally {
      setTemasListLoading(false);
    }
  };
  // Fetch evangelio button configuration
  const fetchEvangelioButtonConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/evangelio-button-config`);
      setEvangelioButtonEnabled(response.data.enabled);
    } catch (error) {
      console.error("Error fetching evangelio button config:", error);
      setEvangelioButtonMessage("Error cargando configuración del botón");
    }
  };

  // Update evangelio button configuration
  const toggleEvangelioButton = async () => {
    setEvangelioButtonLoading(true);
    setEvangelioButtonMessage("");

    try {
      const response = await axios.post(`${API}/admin/evangelio-button-config`, {
        enabled: !evangelioButtonEnabled
      });

      setEvangelioButtonEnabled(response.data.config.enabled);
      setEvangelioButtonMessage(
        `✅ Botón "Evangelio del Día" ${response.data.config.enabled ? "habilitado" : "deshabilitado"} exitosamente`
      );
    } catch (error) {
      console.error("Error toggling evangelio button:", error);
      if (error.response?.data?.detail) {
        setEvangelioButtonMessage(`❌ Error: ${error.response.data.detail}`);
      } else {
        setEvangelioButtonMessage("❌ Error actualizando configuración del botón");
      }
    } finally {
      setEvangelioButtonLoading(false);
    }
  };

  // Fetch topics button configuration
  const fetchTopicsButtonConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/topics-button-config`);
      setTopicsButtonEnabled(response.data.enabled);
    } catch (error) {
      console.error("Error fetching topics button config:", error);
      setTopicsButtonMessage("Error cargando configuración del botón Temas");
    }
  };

  // Update topics button configuration
  const toggleTopicsButton = async () => {
    setTopicsButtonLoading(true);
    setTopicsButtonMessage("");

    try {
      const response = await axios.post(`${API}/admin/topics-button-config`, {
        enabled: !topicsButtonEnabled
      });

      setTopicsButtonEnabled(response.data.config.enabled);
      setTopicsButtonMessage(
        `✅ Botón "Explorar Temas" ${response.data.config.enabled ? "habilitado" : "deshabilitado"} exitosamente`
      );
    } catch (error) {
      console.error("Error toggling topics button:", error);
      if (error.response?.data?.detail) {
        setTopicsButtonMessage(`❌ Error: ${error.response.data.detail}`);
      } else {
        setTopicsButtonMessage("❌ Error actualizando configuración del botón Temas");
      }
    } finally {
      setTopicsButtonLoading(false);
    }
  };

  // Fetch evangelios data
  const fetchEvangelios = async () => {
    setEvangeliosLoading(true);
    try {
      const response = await axios.get(`${API}/admin/evangelios`);
      setEvangelios(response.data.evangelios || []);
    } catch (error) {
      console.error("Error fetching evangelios:", error);
      setEvangelios([]);
    } finally {
      setEvangeliosLoading(false);
    }
  };

  // Upload evangelios JSON file
  const handleEvangelioUpload = async () => {
    if (!evangelioFile) {
      setEvangelioUploadMessage("Por favor, selecciona un archivo JSON.");
      return;
    }

    setEvangelioUploadLoading(true);
    setEvangelioUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("file", evangelioFile);

      const response = await axios.post(`${API}/admin/evangelios/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;
      setEvangelioUploadMessage(
        `✅ Evangelios cargados exitosamente: ${data.entries_count} entradas desde ${data.filename}`
      );
      setEvangelioFile(null);
      // Refresh evangelios list
      fetchEvangelios();
    } catch (error) {
      console.error("Error uploading evangelios:", error);
      if (error.response?.data?.detail) {
        setEvangelioUploadMessage(`❌ Error: ${error.response.data.detail}`);
      } else {
        setEvangelioUploadMessage("❌ Error al cargar el archivo de evangelios.");
      }
    } finally {
      setEvangelioUploadLoading(false);
    }
  };

  // View specific evangelio
  const viewEvangelio = async (date) => {
    try {
      const response = await axios.get(`${API}/admin/evangelios/${date}`);
      setSelectedEvangelio(response.data);
      setEvangelioModalOpen(true);
    } catch (error) {
      console.error("Error fetching evangelio for date:", date, error);
    }
  };

  // Delete all evangelios
  const deleteAllEvangelios = async () => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar todos los evangelios? Esta acción no se puede deshacer.")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/evangelios`);
      setEvangelioUploadMessage("✅ Todos los evangelios eliminados exitosamente.");
      setEvangelios([]);
    } catch (error) {
      console.error("Error deleting evangelios:", error);
      if (error.response?.data?.detail) {
        setEvangelioUploadMessage(`❌ Error al eliminar: ${error.response.data.detail}`);
      } else {
        setEvangelioUploadMessage("❌ Error al eliminar evangelios.");
      }
    }
  };

  const toggleTemaVisibility = async (temaId, currentVisibility) => {
    try {
      const response = await axios.put(`${API}/admin/tema/${temaId}/visibility`, {
        visible: !currentVisibility
      });
      
      setTemasMessage(response.data.message);
      await fetchTemas(); // Refresh list
    } catch (err) {
      console.error("Error toggling tema visibility:", err);
      setTemasMessage(`Error: ${err.response?.data?.detail || "Error actualizando visibilidad"}`);
    }
  };

  const deleteTema = async (temaId, temaTitle) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar el tema "${temaTitle}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/tema/${temaId}`);
      setTemasMessage(response.data.message);
      await fetchTemas(); // Refresh list
    } catch (err) {
      console.error("Error deleting tema:", err);
      setTemasMessage(`Error: ${err.response?.data?.detail || "Error eliminando tema"}`);
    }
  };

  // About Functions
  const fetchAboutContent = async () => {
    setAboutLoading(true);
    setAboutMessage('');
    try {
      const response = await axios.get(`${API}/about`);
      setAboutContent(response.data.content || '');
    } catch (err) {
      console.error("Error fetching about content:", err);
      setAboutMessage("Error cargando contenido");
      setAboutContent('');
    } finally {
      setAboutLoading(false);
    }
  };

  const saveAboutContent = async () => {
    setAboutLoading(true);
    setAboutMessage('');
    
    try {
      const response = await axios.post(`${API}/admin/about`, {
        content: aboutContent
      });
      
      setAboutMessage('✅ Contenido guardado exitosamente');
    } catch (err) {
      console.error("Error saving about content:", err);
      if (err.response?.data?.detail) {
        setAboutMessage(`❌ Error: ${err.response.data.detail}`);
      } else {
        setAboutMessage('❌ Error guardando contenido');
      }
    } finally {
      setAboutLoading(false);
    }
  };

  const handleAboutFileUpload = async () => {
    if (!aboutFile) {
      setAboutMessage('Por favor, selecciona un archivo .md');
      return;
    }

    setAboutUploadLoading(true);
    setAboutMessage('');

    try {
      const formData = new FormData();
      formData.append('file', aboutFile);

      const response = await axios.post(`${API}/admin/about/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setAboutContent(response.data.content);
      setAboutMessage(`✅ Archivo '${response.data.filename}' cargado exitosamente`);
      setAboutFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('aboutFileInput');
      if (fileInput) fileInput.value = '';
      
    } catch (err) {
      console.error("Error uploading about file:", err);
      if (err.response?.data?.detail) {
        setAboutMessage(`❌ Error: ${err.response.data.detail}`);
      } else {
        setAboutMessage('❌ Error subiendo archivo');
      }
    } finally {
      setAboutUploadLoading(false);
    }
  };

  // Delete user functionality
  const handleDeleteUser = (user) => {
    setDeleteConfirmation({ user });
    setDeleteMessage("");
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirmation) return;

    setDeleteLoading(true);
    setDeleteMessage("");

    try {
      const response = await axios.delete(`${API}/admin/user/${deleteConfirmation.user.id}`);
      
      setDeleteMessage(response.data.message);
      setDeleteConfirmation(null);
      
      // Refresh users list
      await fetchUsers(true);
    } catch (err) {
      console.error("Error deleting user:", err);
      setDeleteMessage(`Error: ${err.response?.data?.detail || "Failed to delete user"}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteConfirmation(null);
    setDeleteMessage("");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/json") {
      setSelectedFile(file);
      setUploadMessage("");
    } else {
      setUploadMessage("Por favor selecciona un archivo JSON válido");
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadLoading(true);
    setUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API}/admin/upload-playlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadMessage(`✅ ${response.data.message}`);
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById("fileInput");
      if (fileInput) fileInput.value = "";

      // Refresh playlists list
      const playlistsResponse = await axios.get(`${API}/admin/playlists`);
      setPlaylists(playlistsResponse.data);

    } catch (err) {
      console.error("Upload error:", err);
      setUploadMessage(`❌ Error: ${err.response?.data?.detail || "Error uploading file"}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeletePlaylist = async (volumeNumber) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la playlist del Volumen ${volumeNumber}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/playlists/${volumeNumber}`);
      setPlaylists(playlists.filter(playlist => playlist.volume_number !== volumeNumber));
      setUploadMessage(`✅ Playlist del Volumen ${volumeNumber} eliminada`);
    } catch (err) {
      console.error("Delete error:", err);
      setUploadMessage(`❌ Error eliminando playlist: ${err.response?.data?.detail || "Error"}`);
    }
  };

  // Main files handlers
  const handleMainFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/zip") {
      setMainFileZip(file);
      setMainFileUploadMessage("");
    } else {
      setMainFileUploadMessage("Por favor selecciona un archivo ZIP válido");
      setMainFileZip(null);
    }
  };

  const handleMainFileUpload = async () => {
    if (!mainFileZip) return;

    setMainFileUploadLoading(true);
    setMainFileUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("file", mainFileZip);

      const response = await axios.post(`${API}/admin/upload-main-files`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { volume, files_processed, files_failed, failed_files } = response.data;
      
      let message = `✅ Volumen ${volume}: ${files_processed} archivos procesados`;
      if (files_failed > 0) {
        message += `, ${files_failed} fallidos`;
        if (failed_files.length > 0) {
          message += `\n\nArchivos fallidos:\n${failed_files.slice(0, 5).join('\n')}`;
          if (failed_files.length > 5) {
            message += `\n... y ${failed_files.length - 5} más`;
          }
        }
      }
      
      setMainFileUploadMessage(message);
      setMainFileZip(null);
      
      // Reset file input
      const fileInput = document.getElementById("mainFileInput");
      if (fileInput) fileInput.value = "";

    } catch (err) {
      console.error("Main file upload error:", err);
      setMainFileUploadMessage(`❌ Error: ${err.response?.data?.detail || "Error uploading main files"}`);
    } finally {
      setMainFileUploadLoading(false);
    }
  };

  // Optional files handlers
  const handleOptionalFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/zip") {
      setOptionalFileZip(file);
      setOptionalFileUploadMessage("");
    } else {
      setOptionalFileUploadMessage("Por favor selecciona un archivo ZIP válido");
      setOptionalFileZip(null);
    }
  };

  const handleOptionalFileUpload = async () => {
    if (!optionalFileZip) return;

    setOptionalFileUploadLoading(true);
    setOptionalFileUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("file", optionalFileZip);

      const response = await axios.post(`${API}/admin/upload-optional-files`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { volume, files_processed, files_failed, failed_files } = response.data;
      
      let message = `✅ Volumen ${volume}: ${files_processed} archivos opcionales procesados`;
      if (files_failed > 0) {
        message += `, ${files_failed} fallidos`;
        if (failed_files.length > 0) {
          message += `\n\nArchivos fallidos:\n${failed_files.slice(0, 5).join('\n')}`;
          if (failed_files.length > 5) {
            message += `\n... y ${failed_files.length - 5} más`;
          }
        }
      }
      
      setOptionalFileUploadMessage(message);
      setOptionalFileZip(null);
      
      // Reset file input
      const fileInput = document.getElementById("optionalFileInput");
      if (fileInput) fileInput.value = "";

    } catch (err) {
      console.error("Optional file upload error:", err);
      setOptionalFileUploadMessage(`❌ Error: ${err.response?.data?.detail || "Error uploading optional files"}`);
    } finally {
      setOptionalFileUploadLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!isAuthenticated) {
    return <div>Redirecting...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-purple-600 text-white py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className="flex items-center text-white hover:text-purple-200 transition-colors duration-300"
              >
                <ArrowLeft size={20} className="mr-2" />
                Volver al inicio
              </button>
              <h1 className="text-2xl font-bold">Panel de Administración</h1>
            </div>
            <button
              onClick={handleLogout}
              className="bg-purple-700 hover:bg-purple-800 px-4 py-2 rounded-lg transition-colors duration-300"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("playlists")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "playlists"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Video className="inline mr-2" size={20} />
                Gestión de Playlists
              </button>
              <button
                onClick={() => setActiveTab("main-files")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "main-files"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Archive className="inline mr-2" size={20} />
                Archivos Principales
              </button>
              <button
                onClick={() => setActiveTab("imagen")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "imagen"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Image className="inline mr-2" size={20} />
                Imagen
              </button>
              <button
                onClick={() => setActiveTab("evangelio")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "evangelio"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FileText className="inline mr-2" size={20} />
                Evangelio
              </button>
              <button
                onClick={() => setActiveTab("users")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "users"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Users className="inline mr-2" size={20} />
                Gestión de Usuarios
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "settings"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Settings className="inline mr-2" size={20} />
                Configuración
              </button>
              <button
                onClick={() => setActiveTab("about")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                  activeTab === "about"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FileText className="inline mr-2" size={20} />
                Acerca de
              </button>
            </nav>
          </div>
        </div>

        {/* Playlists Tab */}
        {activeTab === "playlists" && (
          <>
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Upload className="mr-2" size={24} />
                Subir Playlist JSON
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="fileInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo JSON
                  </label>
                  <input
                    type="file"
                    id="fileInput"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {selectedFile && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <FileText size={16} className="inline mr-1" />
                      Archivo seleccionado: <span className="font-medium">{selectedFile.name}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}

                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploadLoading}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
                >
                  {uploadLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Subir archivo
                    </>
                  )}
                </button>

                {uploadMessage && (
                  <div className={`p-4 rounded-lg ${
                    uploadMessage.startsWith("✅") 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    {uploadMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Playlists List */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Video className="mr-2" size={24} />
                Playlists Subidas
              </h2>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Cargando playlists...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {error}
                </div>
              ) : playlists.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Video size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No hay playlists subidas aún</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {playlists.map((playlist) => (
                    <div
                      key={playlist.volume_number}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-purple-600">
                            {playlist.playlist_title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <Video size={16} className="inline mr-1" />
                            {playlist.videos_count} videos
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            <Calendar size={16} className="inline mr-1" />
                            Subido: {new Date(playlist.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/volume/${playlist.volume_number}`)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors duration-300"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => handleDeletePlaylist(playlist.volume_number)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors duration-300 flex items-center"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                Instrucciones para subir playlists
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• El archivo debe ser un JSON válido con la estructura de playlist de YouTube</li>
                <li>• El título de la playlist debe contener "Volumen X" donde X es el número del volumen</li>
                <li>• El archivo debe incluir: playlistId, playlistTitle, description, e items (videos)</li>
                <li>• Cada video debe tener: title, videoId, url, channelTitle, y thumbnail</li>
                <li>• Si ya existe una playlist para ese volumen, será reemplazada</li>
              </ul>
            </div>
          </>
        )}

        {/* Main Files Tab */}
        {activeTab === "main-files" && (
          <>
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Archive className="mr-2" size={24} />
                Subir Archivos Principales (ZIP)
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="mainFileInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo ZIP
                  </label>
                  <input
                    type="file"
                    id="mainFileInput"
                    accept=".zip"
                    onChange={handleMainFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {mainFileZip && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <Archive size={16} className="inline mr-1" />
                      Archivo seleccionado: <span className="font-medium">{mainFileZip.name}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Tamaño: {(mainFileZip.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}

                <button
                  onClick={handleMainFileUpload}
                  disabled={!mainFileZip || mainFileUploadLoading}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
                >
                  {mainFileUploadLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Subir archivo ZIP
                    </>
                  )}
                </button>

                {mainFileUploadMessage && (
                  <div className={`p-4 rounded-lg ${
                    mainFileUploadMessage.startsWith("✅") 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    <pre className="whitespace-pre-wrap text-sm">{mainFileUploadMessage}</pre>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                Instrucciones para subir archivos principales
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• El archivo ZIP debe tener el formato: <strong>Vol_&lt;volumen&gt;_main.zip</strong> (ejemplo: Vol_11_main.zip)</li>
                <li>• Dentro del ZIP, cada archivo JSON debe tener el formato: <strong>&lt;volumen&gt;-&lt;índice&gt;.json</strong> (ejemplo: 11-1.json, 11-2.json)</li>
                <li>• Cada archivo JSON debe contener la estructura: info (volume, index, date), dialogue (speaker, text), description</li>
                <li>• El volumen en el nombre del ZIP debe coincidir con el volumen en el contenido de los archivos JSON</li>
                <li>• Se procesarán aproximadamente 2500 archivos JSON distribuidos en múltiples volúmenes</li>
                <li>• Los archivos duplicados serán reemplazados</li>
              </ul>
            </div>

            {/* Optional Files Upload Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Archive className="mr-2" size={24} />
                Subir Archivos Opcionales (ZIP)
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="optionalFileInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo ZIP opcional
                  </label>
                  <input
                    type="file"
                    id="optionalFileInput"
                    accept=".zip"
                    onChange={handleOptionalFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {optionalFileZip && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <Archive size={16} className="inline mr-1" />
                      Archivo seleccionado: <span className="font-medium">{optionalFileZip.name}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Tamaño: {(optionalFileZip.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}

                <button
                  onClick={handleOptionalFileUpload}
                  disabled={!optionalFileZip || optionalFileUploadLoading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
                >
                  {optionalFileUploadLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Subir archivo ZIP opcional
                    </>
                  )}
                </button>

                {optionalFileUploadMessage && (
                  <div className={`p-4 rounded-lg ${
                    optionalFileUploadMessage.startsWith("✅") 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    <pre className="whitespace-pre-wrap text-sm">{optionalFileUploadMessage}</pre>
                  </div>
                )}
              </div>
            </div>

            {/* Optional Files Instructions */}
            <div className="bg-green-50 rounded-lg p-6 mt-4">
              <h3 className="text-lg font-semibold text-green-800 mb-3">
                Instrucciones para subir archivos opcionales
              </h3>
              <ul className="text-sm text-green-700 space-y-2">
                <li>• El archivo ZIP debe tener el formato: <strong>Vol_&lt;volumen&gt;_optional.zip</strong> (ejemplo: Vol_11_optional.zip)</li>
                <li>• Dentro del ZIP, cada archivo JSON debe tener el formato: <strong>&lt;volumen&gt;-&lt;índice&gt;_o.json</strong> (ejemplo: 11-1_o.json, 11-2_o.json)</li>
                <li>• Cada archivo JSON debe contener: info, dialogue_summary, keywords, topics, quiz, new_testament, homework, bulletpoints</li>
                <li>• Los archivos opcionales complementan los archivos principales con contenido educativo adicional</li>
                <li>• Quiz incluye preguntas, respuestas y explicaciones para el aprendizaje interactivo</li>
                <li>• Los archivos duplicados serán reemplazados</li>
              </ul>
            </div>

            {/* Temas Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <Archive className="mr-2" size={24} />
                Subir Archivos Temas (ZIP)
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="temasFileInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo ZIP con temas
                  </label>
                  <input
                    type="file"
                    id="temasFileInput"
                    accept=".zip"
                    onChange={handleTemasFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {temasFile && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <Archive size={16} className="inline mr-1" />
                      Archivo seleccionado: <span className="font-medium">{temasFile.name}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Tamaño: {(temasFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}

                <button
                  onClick={handleTemasUpload}
                  disabled={!temasFile || temasUploading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
                >
                  {temasUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Subir archivo ZIP de temas
                    </>
                  )}
                </button>

                {temasMessage && (
                  <div className={`p-4 rounded-lg ${
                    temasMessage.includes("exitosamente") 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    <pre className="whitespace-pre-wrap text-sm">{temasMessage}</pre>
                  </div>
                )}
              </div>
            </div>

            {/* Temas Management */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <FileText className="mr-2" size={24} />
                Gestión de Temas
              </h2>
              
              {temasListLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Cargando temas...</p>
                </div>
              ) : temas.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No hay temas disponibles</p>
                  <p className="text-sm">Sube un archivo ZIP para comenzar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Título
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha de Creación
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {temas.map((tema) => (
                        <tr key={tema.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {tema.title}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(tema.created_at).toLocaleDateString('es-ES')}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              tema.visible 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {tema.visible ? 'Visible' : 'Oculto'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => toggleTemaVisibility(tema.id, tema.visible)}
                              className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${
                                tema.visible
                                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              <Eye size={12} className="mr-1" />
                              {tema.visible ? 'Ocultar' : 'Mostrar'}
                            </button>
                            <button
                              onClick={() => deleteTema(tema.id, tema.title)}
                              className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <Trash2 size={12} className="mr-1" />
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Temas Instructions */}
            <div className="bg-blue-50 rounded-lg p-6 mt-4">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                Instrucciones para Temas
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• El archivo ZIP debe contener archivos JSON con estructura de temas</li>
                <li>• Cada JSON debe tener: <strong>title</strong> (string) e <strong>items</strong> (array)</li>
                <li>• Cada item debe contener: <strong>info</strong> (volume, index, date, link) y <strong>dialogue_summary</strong> (array)</li>
                <li>• Si existe un tema con el mismo título, será reemplazado</li>
                <li>• Los archivos son codificados automáticamente para seguridad</li>
                <li>• Solo usuarios autenticados podrán ver los temas en el frontend</li>
              </ul>
            </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <Users className="mr-2" size={24} />
                Usuarios Registrados
              </h2>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    console.log('TEST CSV button clicked in users tab!');
                    alert('CSV button clicked from users tab! Check console for details.');
                    downloadUsersCSV();
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center"
                >
                  <Download size={16} className="mr-2" />
                  Descargar CSV
                </button>
                <button
                  onClick={() => fetchUsers(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 flex items-center"
                >
                  <Activity size={16} className="mr-2" />
                  Actualizar
                </button>
              </div>
            </div>

            {usersLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando usuarios...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No hay usuarios registrados aún</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Users className="text-purple-600 mr-3" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Total Usuarios</p>
                        <p className="text-2xl font-bold text-purple-600">{usersPagination.total_count}</p>
                        <p className="text-xs text-gray-500">Mostrando {users.length} de {usersPagination.total_count}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Eye className="text-green-600 mr-3" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Total Visualizaciones</p>
                        <p className="text-2xl font-bold text-green-600">
                          {userStatistics.total_visualizations}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Activity className="text-blue-600 mr-3" size={24} />
                      <div>
                        <p className="text-sm text-gray-600">Usuarios Activos</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {userStatistics.active_users}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usuario
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Videos Vistos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Volúmenes Accedidos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Última Actividad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Registrado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-purple-600">
                                    {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.first_name} {user.last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {user.is_active ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Activo
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Inactivo
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Eye className="text-gray-400 mr-2" size={16} />
                              <span className="text-sm font-medium text-gray-900">
                                {user.total_videos_watched}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Video className="text-gray-400 mr-2" size={16} />
                              <span className="text-sm text-gray-900">
                                {user.volumes_accessed} volúmenes
                              </span>
                              {user.volumes_list.length > 0 && (
                                <div className="ml-2">
                                  <span className="text-xs text-gray-500">
                                    ({user.volumes_list.join(", ")})
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.last_activity ? (
                              new Date(user.last_activity).toLocaleDateString("es-ES", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })
                            ) : (
                              "Sin actividad"
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString("es-ES", {
                              year: "numeric",
                              month: "short",
                              day: "numeric"
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-800 transition-colors duration-300 flex items-center"
                              title={`Eliminar usuario ${user.first_name} ${user.last_name}`}
                            >
                              <UserX size={16} className="mr-1" />
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Loading More Indicator */}
                {loadingMoreUsers && (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">Cargando más usuarios...</p>
                  </div>
                )}

                {/* Error Message for Load More */}
                {usersError && (
                  <div className="text-center py-4">
                    <div className="bg-red-100 text-red-700 p-3 rounded-lg">
                      <p className="text-sm">{usersError}</p>
                      <button
                        onClick={() => loadMoreUsers()}
                        className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors duration-300"
                      >
                        Reintentar
                      </button>
                    </div>
                  </div>
                )}

                {/* End of List Indicator */}
                {!usersPagination.has_more && users.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">Has llegado al final de la lista</p>
                  </div>
                )}

                {/* Scroll Sentinel for Infinite Scroll */}
                <div id="users-scroll-sentinel" className="h-4"></div>
              </>
            )}
            {deleteMessage && (
              <div className={`mt-4 p-4 rounded-lg ${
                deleteMessage.includes("successfully") 
                  ? "bg-green-100 text-green-700" 
                  : "bg-red-100 text-red-700"
              }`}>
                {deleteMessage}
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <UserX className="text-red-600 mr-3" size={24} />
                <h3 className="text-lg font-semibold text-gray-800">
                  Confirmar Eliminación
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que quieres eliminar al usuario{" "}
                <strong>
                  {deleteConfirmation.user.first_name} {deleteConfirmation.user.last_name}
                </strong>{" "}
                ({deleteConfirmation.user.email})?
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  <strong>⚠️ Esta acción no se puede deshacer.</strong> Se eliminarán:
                </p>
                <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                  <li>El usuario y su información personal</li>
                  <li>Todo su historial de videos vistos</li>
                  <li>Todas sus notas personales</li>
                  <li>Tokens de verificación y reseteo</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={cancelDeleteUser}
                  disabled={deleteLoading}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteUser}
                  disabled={deleteLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center"
                >
                  {deleteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <UserX size={16} className="mr-2" />
                      Eliminar Usuario
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <Settings className="mr-2" size={24} />
                Configuración del Sistema
              </h2>
              <button
                onClick={() => {
                  console.log('CSV button clicked!');
                  alert('CSV button clicked! Check console for details.');
                  downloadUsersCSV();
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center"
              >
                <Download size={16} className="mr-2" />
                Descargar CSV Usuarios
              </button>
            </div>

            {/* Email Validation Toggle */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                Validación de Correo Electrónico
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {emailValidationEnabled 
                      ? "La validación de correo está ACTIVADA. Los usuarios deben verificar su correo antes de poder acceder."
                      : "La validación de correo está DESACTIVADA. Los usuarios pueden acceder inmediatamente después del registro."
                    }
                  </p>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700 mr-3">
                      Estado actual:
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      emailValidationEnabled 
                        ? "bg-green-100 text-green-800" 
                        : "bg-yellow-100 text-yellow-800"
                    }`}>
                      {emailValidationEnabled ? "Activada" : "Desactivada"}
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={toggleEmailValidation}
                  disabled={emailValidationLoading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                    emailValidationEnabled
                      ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {emailValidationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Actualizando...
                    </>
                  ) : (
                    emailValidationEnabled ? "Desactivar" : "Activar"
                  )}
                </button>
              </div>

              {emailValidationMessage && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  emailValidationMessage.includes("successfully") 
                    ? "bg-green-100 text-green-700" 
                    : "bg-red-100 text-red-700"
                }`}>
                  {emailValidationMessage}
                </div>
              )}
            </div>

            {/* Topics Button Configuration Section */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                Visibilidad del Botón "Explorar Temas"
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {topicsButtonEnabled 
                      ? "El botón \"Explorar Temas\" está VISIBLE en la página principal."
                      : "El botón \"Explorar Temas\" está OCULTO en la página principal."
                    }
                  </p>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700 mr-3">
                      Estado actual:
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      topicsButtonEnabled 
                        ? "bg-green-100 text-green-800" 
                        : "bg-red-100 text-red-800"
                    }`}>
                      {topicsButtonEnabled ? "✅ Visible" : "❌ Oculto"}
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={toggleTopicsButton}
                  disabled={topicsButtonLoading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                    topicsButtonEnabled
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {topicsButtonLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Actualizando...
                    </>
                  ) : (
                    topicsButtonEnabled ? "Ocultar Botón" : "Mostrar Botón"
                  )}
                </button>
              </div>

              {topicsButtonMessage && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  topicsButtonMessage.includes("✅") 
                    ? "bg-green-100 text-green-700" 
                    : "bg-red-100 text-red-700"
                }`}>
                  {topicsButtonMessage}
                </div>
              )}
            </div>

            {/* News Configuration Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                📰 Secciones de Noticias
              </h3>
              
              {/* Public News Section */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h4 className="text-lg font-medium text-gray-700 mb-4">📢 Sección Pública</h4>
                <p className="text-sm text-gray-600 mb-4">Visible para todos los usuarios (incluidos los no registrados)</p>
                
                <div className="space-y-4">
                  {/* Public Enable Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Activar sección pública</span>
                    <button
                      onClick={() => setNewsConfig(prev => ({
                        ...prev,
                        public: { ...prev.public, enabled: !prev.public.enabled }
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
                        newsConfig.public.enabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                          newsConfig.public.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* Public Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título (soporta markdown) *
                    </label>
                    <input
                      type="text"
                      value={newsConfig.public.title}
                      onChange={(e) => setNewsConfig(prev => ({
                        ...prev,
                        public: { ...prev.public, title: e.target.value }
                      }))}
                      placeholder="**Título en negrita** o [enlace](https://ejemplo.com)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      maxLength="200"
                    />
                    <div className="text-xs text-gray-500 mt-1">{newsConfig.public.title.length}/200 caracteres</div>
                  </div>
                  
                  {/* Public Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción (soporta markdown)
                    </label>
                    <textarea
                      value={newsConfig.public.description}
                      onChange={(e) => setNewsConfig(prev => ({
                        ...prev,
                        public: { ...prev.public, description: e.target.value }
                      }))}
                      placeholder="Texto con *énfasis*, **negrita**, y [enlaces](https://ejemplo.com)"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      maxLength="1000"
                    />
                    <div className="text-xs text-gray-500 mt-1">{newsConfig.public.description.length}/1000 caracteres</div>
                  </div>
                </div>
              </div>

              {/* Private News Section */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h4 className="text-lg font-medium text-gray-700 mb-4">🔒 Sección Privada</h4>
                <p className="text-sm text-gray-600 mb-4">Visible solo para usuarios registrados e iniciados sesión</p>
                
                <div className="space-y-4">
                  {/* Private Enable Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Activar sección privada</span>
                    <button
                      onClick={() => setNewsConfig(prev => ({
                        ...prev,
                        private: { ...prev.private, enabled: !prev.private.enabled }
                      }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
                        newsConfig.private.enabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                          newsConfig.private.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* Private Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título (soporta markdown) *
                    </label>
                    <input
                      type="text"
                      value={newsConfig.private.title}
                      onChange={(e) => setNewsConfig(prev => ({
                        ...prev,
                        private: { ...prev.private, title: e.target.value }
                      }))}
                      placeholder="**Contenido exclusivo** para [miembros](/volumes)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      maxLength="200"
                    />
                    <div className="text-xs text-gray-500 mt-1">{newsConfig.private.title.length}/200 caracteres</div>
                  </div>
                  
                  {/* Private Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción (soporta markdown)
                    </label>
                    <textarea
                      value={newsConfig.private.description}
                      onChange={(e) => setNewsConfig(prev => ({
                        ...prev,
                        private: { ...prev.private, description: e.target.value }
                      }))}
                      placeholder="Gracias por iniciar sesión. Accede a recursos especiales aquí."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      maxLength="1000"
                    />
                    <div className="text-xs text-gray-500 mt-1">{newsConfig.private.description.length}/1000 caracteres</div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-between">
                <div>
                  <button
                    onClick={saveNewsConfig}
                    disabled={newsConfigLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {newsConfigLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Guardando...
                      </>
                    ) : (
                      'Guardar Configuración'
                    )}
                  </button>
                </div>
              </div>

              {newsConfigMessage && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  newsConfigMessage.includes("exitosamente") 
                    ? "bg-green-100 text-green-700" 
                    : "bg-red-100 text-red-700"
                }`}>
                  {newsConfigMessage}
                </div>
              )}

              {/* Markdown Help */}
              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  📝 Ayuda de Markdown
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><code>**texto**</code> → <strong>negrita</strong></div>
                  <div><code>*texto*</code> → <em>cursiva</em></div>
                  <div><code>[enlace](https://ejemplo.com)</code> → <a href="#" className="text-purple-600">enlace</a></div>
                  <div><code>- elemento</code> → lista con viñetas</div>
                  <div><code>1. elemento</code> → lista numerada</div>
                </div>
              </div>
            </div>

            {/* Additional Settings Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                ℹ️ Información Importante
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Cuando la validación está <strong>activada</strong>, se envían emails de verificación.</li>
                <li>• Cuando la validación está <strong>desactivada</strong>, se envían emails de bienvenida.</li>
                <li>• Los cambios se aplican inmediatamente a nuevos registros.</li>
                <li>• <strong>Al desactivar la validación</strong>: Todos los usuarios no verificados existentes se verifican automáticamente.</li>
                <li>• Los usuarios no verificados pueden acceder inmediatamente cuando la validación está desactivada.</li>
              </ul>
            </div>
          </div>
        )}

        {/* Imagen Tab */}
        {activeTab === "imagen" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              <Image className="inline mr-2" size={24} />
              Configuración de Imagen Principal
            </h2>

            <div className="space-y-6">
              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de la Imagen *
                </label>
                <input
                  type="url"
                  value={imageConfig.imageUrl}
                  onChange={(e) => setImageConfig(prev => ({
                    ...prev,
                    imageUrl: e.target.value
                  }))}
                  placeholder="https://res.cloudinary.com/djypbnlqf/image/upload/01.10.38.35_Padre_Celestial_qdpy2n.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              {/* Click URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de Destino (Click) *
                </label>
                <input
                  type="url"
                  value={imageConfig.clickUrl}
                  onChange={(e) => setImageConfig(prev => ({
                    ...prev,
                    clickUrl: e.target.value
                  }))}
                  placeholder="https://youtube.com/playlist?list=PLl_WmU7uJsJeKBznNXEE5XnQPFp0yMQxN&si=OwhRIDaRk2XHj0eM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              {/* Tooltip Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texto de Ayuda (Tooltip)
                </label>
                <input
                  type="text"
                  value={imageConfig.tooltipText}
                  onChange={(e) => setImageConfig(prev => ({
                    ...prev,
                    tooltipText: e.target.value
                  }))}
                  placeholder="Click to watch playlist"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Alt Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texto Alternativo (Alt)
                </label>
                <input
                  type="text"
                  value={imageConfig.altText}
                  onChange={(e) => setImageConfig(prev => ({
                    ...prev,
                    altText: e.target.value
                  }))}
                  placeholder="Featured Image"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Visible Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">Mostrar Imagen</span>
                  <p className="text-xs text-gray-500">Controla si la imagen es visible en la página principal</p>
                </div>
                <button
                  onClick={() => setImageConfig(prev => ({
                    ...prev,
                    visible: !prev.visible
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
                    imageConfig.visible ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                      imageConfig.visible ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Preview */}
              {imageConfig.imageUrl && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Vista Previa</h4>
                  <div className="text-center">
                    <img
                      src={imageConfig.imageUrl}
                      alt={imageConfig.altText}
                      title={imageConfig.tooltipText}
                      className="max-w-full h-auto mx-auto rounded-lg cursor-pointer hover:scale-105 transition-transform duration-300"
                      style={{
                        maxHeight: '300px',
                        width: 'auto'
                      }}
                      onClick={() => window.open(imageConfig.clickUrl, '_blank')}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <div style={{display: 'none'}} className="text-red-500 text-sm">
                      Error cargando imagen. Verifica la URL.
                    </div>
                  </div>
                  {imageConfig.tooltipText && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Tooltip: "{imageConfig.tooltipText}"
                    </p>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="flex items-center justify-between">
                <button
                  onClick={saveImageConfig}
                  disabled={imageConfigLoading || !imageConfig.imageUrl.trim() || !imageConfig.clickUrl.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {imageConfigLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Guardando...
                    </>
                  ) : (
                    'Guardar Configuración'
                  )}
                </button>
              </div>

              {/* Success/Error Message */}
              {imageConfigMessage && (
                <div className={`p-3 rounded-lg text-sm ${
                  imageConfigMessage.includes("exitosamente") 
                    ? "bg-green-100 text-green-700" 
                    : "bg-red-100 text-red-700"
                }`}>
                  {imageConfigMessage}
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">
                  ℹ️ Información de Uso
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• La imagen aparecerá antes de la lista de volúmenes en la página principal</li>
                  <li>• En móviles ocupará el 100% del ancho, en escritorio el 50%</li>
                  <li>• Al hacer clic en la imagen se abrirá la URL de destino en una nueva pestaña</li>
                  <li>• Usa URLs completas incluyendo https:// para ambos campos</li>
                  <li>• La imagen debe estar alojada en un servidor externo (ej: Cloudinary)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Evangelio Tab */}
        {activeTab === "evangelio" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              <FileText className="inline mr-2" size={24} />
              Gestión de Evangelios
            </h2>

            {/* Button Visibility Control */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold mb-4 text-blue-800">Control de Visibilidad</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800">Botón "Evangelio del Día" en Homepage</h4>
                  <p className="text-sm text-gray-600">
                    Controla si los usuarios pueden ver el botón "Evangelio del Día" en la página principal
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    evangelioButtonEnabled 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {evangelioButtonEnabled ? '✅ Visible' : '❌ Oculto'}
                  </div>
                  
                  <button
                    onClick={toggleEvangelioButton}
                    disabled={evangelioButtonLoading}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                      evangelioButtonEnabled
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {evangelioButtonLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Actualizando...
                      </>
                    ) : (
                      <>
                        {evangelioButtonEnabled ? (
                          <>
                            <Eye className="inline mr-1" size={16} />
                            Ocultar Botón
                          </>
                        ) : (
                          <>
                            <Eye className="inline mr-1" size={16} />
                            Mostrar Botón
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {evangelioButtonMessage && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  evangelioButtonMessage.includes("✅") 
                    ? "bg-green-100 text-green-700" 
                    : "bg-red-100 text-red-700"
                }`}>
                  {evangelioButtonMessage}
                </div>
              )}
            </div>

            {/* Upload Section */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Cargar Archivo JSON de Evangelios</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Archivo JSON *
                  </label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => setEvangelioFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona un archivo JSON con los evangelios del día
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleEvangelioUpload}
                    disabled={evangelioUploadLoading || !evangelioFile}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {evangelioUploadLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Cargando...
                      </>
                    ) : (
                      <>
                        <Upload className="inline mr-2" size={16} />
                        Cargar Evangelios
                      </>
                    )}
                  </button>

                  {evangelios.length > 0 && (
                    <button
                      onClick={deleteAllEvangelios}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
                    >
                      <Trash2 className="inline mr-2" size={16} />
                      Eliminar Todos
                    </button>
                  )}
                </div>

                {evangelioUploadMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    evangelioUploadMessage.includes("✅") 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    {evangelioUploadMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Evangelios List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Evangelios Disponibles</h3>
                <span className="text-sm text-gray-500">
                  {evangelios.length} {evangelios.length === 1 ? 'evangelio' : 'evangelios'}
                </span>
              </div>

              {evangeliosLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Cargando evangelios...</p>
                </div>
              ) : evangelios.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                  <p className="text-gray-500">No hay evangelios cargados</p>
                  <p className="text-sm text-gray-400">Sube un archivo JSON para comenzar</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {evangelios.map((evangelio, index) => (
                    <div
                      key={evangelio.date || index}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => viewEvangelio(evangelio.date)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-purple-600">
                          <Calendar className="inline mr-1" size={16} />
                          {evangelio.date}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {evangelio.weekday}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <h4 className="font-medium">{evangelio.gospel?.heading || 'Sin título'}</h4>
                        <p className="text-xs text-gray-500">{evangelio.gospel?.reference || ''}</p>
                      </div>
                      
                      <div className="text-xs text-gray-400">
                        Click para ver detalles
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                ℹ️ Instrucciones de Uso
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Sube un archivo JSON con los evangelios organizados por fecha</li>
                <li>• Cada entrada debe tener los campos: date, gospel (con heading, reference, verses)</li>
                <li>• Los evangelios aparecerán disponibles en el botón "Evangelio del Día" del sitio principal</li>
                <li>• Haz clic en cualquier fecha para ver el evangelio completo</li>
                <li>• Los datos se guardan en la base de datos y persisten entre despliegues</li>
              </ul>
            </div>
          </div>
        )}

        {/* About Tab */}
        {activeTab === "about" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              <FileText className="inline mr-2" size={24} />
              Gestión de Contenido "Acerca de"
            </h2>

            {/* Upload Section */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Subir Archivo Markdown</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="aboutFileInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar archivo .md
                  </label>
                  <input
                    type="file"
                    id="aboutFileInput"
                    accept=".md"
                    onChange={(e) => setAboutFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {aboutFile && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <FileText size={16} className="inline mr-1" />
                      Archivo seleccionado: <span className="font-medium">{aboutFile.name}</span>
                    </p>
                  </div>
                )}

                <button
                  onClick={handleAboutFileUpload}
                  disabled={!aboutFile || aboutUploadLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {aboutUploadLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Subiendo...
                    </>
                  ) : (
                    'Subir Archivo'
                  )}
                </button>
              </div>
            </div>

            {/* Content Editor */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Editor de Contenido</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenido Markdown
                  </label>
                  <textarea
                    value={aboutContent}
                    onChange={(e) => setAboutContent(e.target.value)}
                    placeholder="Escribe aquí el contenido en formato Markdown..."
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {aboutContent.length} caracteres
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={saveAboutContent}
                    disabled={aboutLoading || !aboutContent.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aboutLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Guardando...
                      </>
                    ) : (
                      'Guardar Contenido'
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Success/Error Message */}
            {aboutMessage && (
              <div className={`mb-6 p-3 rounded-lg text-sm ${
                aboutMessage.includes("exitosamente") 
                  ? "bg-green-100 text-green-700" 
                  : "bg-red-100 text-red-700"
              }`}>
                {aboutMessage}
              </div>
            )}

            {/* Markdown Help */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">
                📝 Guía de Markdown
              </h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong># Título grande</strong> - Para títulos principales</p>
                <p><strong>## Título mediano</strong> - Para subtítulos</p>
                <p><strong>**texto en negrita**</strong> - Para texto en negrita</p>
                <p><strong>*texto en cursiva*</strong> - Para texto en cursiva</p>
                <p><strong>[texto del enlace](URL)</strong> - Para crear enlaces</p>
                <p><strong>![descripción](URL_imagen)</strong> - Para insertar imágenes</p>
                <p><strong>- elemento de lista</strong> - Para listas con viñetas</p>
                <p><strong>`código`</strong> - Para texto de código</p>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                ℹ️ Información de Uso
              </h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• El contenido aparecerá cuando los usuarios hagan clic en el botón "Acerca de" en la página principal</li>
                <li>• Puedes subir un archivo .md o editar directamente en el área de texto</li>
                <li>• El contenido se guarda en la base de datos y persiste entre despliegues</li>
                <li>• Soporta Markdown completo incluyendo imágenes, enlaces y formato</li>
                <li>• El contenido se renderiza automáticamente en el modal para los usuarios</li>
              </ul>
            </div>
          </div>
        )}

        {/* Evangelio Modal */}
        {evangelioModalOpen && selectedEvangelio && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  Evangelio del {selectedEvangelio.date} - {selectedEvangelio.weekday}
                </h3>
                <button
                  onClick={() => setEvangelioModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-purple-700 mb-2">
                    {selectedEvangelio.gospel?.heading || 'Sin título'}
                  </h2>
                  <p className="text-lg text-gray-600 italic">
                    {selectedEvangelio.gospel?.reference || ''}
                  </p>
                </div>

                {selectedEvangelio.gospel?.intro && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                    <p className="text-gray-700 italic">
                      {selectedEvangelio.gospel.intro}
                    </p>
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  {selectedEvangelio.gospel?.verses && selectedEvangelio.gospel.verses.length > 0 ? (
                    selectedEvangelio.gospel.verses.map((verse, index) => (
                      <p key={index} className="text-gray-800 leading-relaxed">
                        {verse}
                      </p>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No hay versículos disponibles</p>
                  )}
                </div>

                {selectedEvangelio.gospel?.links && selectedEvangelio.gospel.links.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-2">Enlaces relacionados:</h4>
                    <ul className="space-y-1">
                      {selectedEvangelio.gospel.links.map((link, index) => (
                        <li key={index}>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-800 underline text-sm"
                          >
                            {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  {selectedEvangelio.liturgical_note && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Nota litúrgica:</span> {selectedEvangelio.liturgical_note}
                    </p>
                  )}
                  {selectedEvangelio.url && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Fuente:</span>{' '}
                      <a 
                        href={selectedEvangelio.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 underline"
                      >
                        Ver original
                      </a>
                    </p>
                  )}
                  {selectedEvangelio.copyright_note && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Derechos:</span> {selectedEvangelio.copyright_note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;